import { Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { useFilterSetsSessionStore } from 'hooks/state/usePersistedStore';
import { useFilterSet } from 'hooks/useFilterSet';
import { FormProvider, useForm } from 'react-hook-form';
import { FormFilterList } from '../parts/FormFilterList';
import { AdvancedFilterData } from '../FilterFormController';
import { FilterDialogProps } from '../types';
import { DIALOG_STEPS } from './constants';
import { Button } from 'components/Button';

interface RenameDialogProps extends FilterDialogProps {
  filterSetToRename: string | null;
}

export function RenameDialog({ open, setDialog, filterSetToRename }: RenameDialogProps) {
  const intl = useIntl();
  const [filterSetName, setFilterSetName] = useState('');
  const [initialized, setInitialized] = useState(false);

  const { setSelectedFilterSet } = useFilterSetsSessionStore();
  const { updateFilterSet, getFilterSet } = useFilterSet();

  const filterSetData = useMemo(() => {
    if (!filterSetToRename) return null;
    return getFilterSet(filterSetToRename);
  }, [filterSetToRename, getFilterSet]);

  const methods = useForm<AdvancedFilterData>({
    defaultValues: filterSetData?.content as AdvancedFilterData,
  });

  const handleErrorTranslation = (error: string) => {
    if (error.startsWith('advanced-filter-error-')) {
      return intl.formatMessage({ id: error, defaultMessage: error });
    }
    return error;
  };

  useEffect(() => {
    if (open && filterSetToRename && !initialized) {
      const filterSet = getFilterSet(filterSetToRename);
      if (filterSet) {
        setFilterSetName(filterSet.name);
        setInitialized(true);
      }
    }

    // Reset when dialog closes
    if (!open && initialized) {
      setFilterSetName('');
      setInitialized(false);
    }
  }, [open, filterSetToRename, initialized, getFilterSet]);

  const handleRename = async () => {
    if (!filterSetToRename || !filterSetName.trim()) {
      return;
    }

    const originalFilterSet = getFilterSet(filterSetToRename);
    if (!originalFilterSet) {
      toast.error(
        intl.formatMessage({
          id: 'advanced-filter-error-set-not-found',
          defaultMessage: 'Filter set not found.',
        })
      );
      return;
    }

    // Use original content - only update the name
    const result = await updateFilterSet(
      filterSetToRename,
      filterSetName.trim(),
      originalFilterSet.content as AdvancedFilterData
    );

    if (!result.success) {
      const errorMessage = result.error
        ? handleErrorTranslation(result.error)
        : intl.formatMessage({
            id: 'advanced-filter-error-renaming',
            defaultMessage: 'Error renaming the filter set.',
          });
      toast.error(errorMessage);
      return;
    }

    setSelectedFilterSet(result.filterSet?.filterSetId ?? null);

    // Reset and return to main dialog
    setFilterSetName('');
    setInitialized(false);
    setDialog(DIALOG_STEPS.FILTER_SELECT);
  };

  const handleClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick') return;
    setDialog(DIALOG_STEPS.FILTER_SELECT);
  };

  const isFormValid = filterSetName.trim().length > 0;

  return (
    /* eslint-disable-next-line react/jsx-props-no-spreading */
    <FormProvider {...methods}>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        disableRestoreFocus
        keepMounted={false}
        aria-labelledby="rename-filter-dialog-title"
      >
        <DialogTitle id="rename-filter-dialog-title">
          <FormattedMessage id="advanced-filter-set-rename-dialog.title" defaultMessage="Rename Filter Set" />
          <Button
            icon="Cross"
            aria-label="close"
            onClick={() => setDialog(DIALOG_STEPS.FILTER_SELECT)}
            className="ml-auto"
            tabIndex={0}
          />
        </DialogTitle>

        <DialogContent>
          <div>
            <Typography variant="h4" sx={{ pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <FormattedMessage id="advanced-filter-set-name" defaultMessage="Filter set name" />
            </Typography>

            <TextField
              autoFocus
              fullWidth
              variant="outlined"
              size="small"
              value={filterSetName}
              onChange={e => setFilterSetName(e.target.value)}
              placeholder={intl.formatMessage({
                id: 'advanced-filter-set-name',
                defaultMessage: 'Filter set name',
              })}
              className="mt-4"
              inputProps={{ maxLength: 50 }}
              onKeyDown={e => {
                if (e.key === 'Enter' && isFormValid) {
                  handleRename();
                }
              }}
            />

            <div className="flex justify-end mt-1">
              <span className="text-xs text-gray-500">
                {filterSetName.length}/50 {intl.formatMessage({ id: 'general.math.chars', defaultMessage: 'chars' })}
              </span>
            </div>
          </div>
          {filterSetData && <FormFilterList formContext={methods} />}
        </DialogContent>

        <DialogActions>
          <Button
            id="rename-filter-set-cancel-button"
            priority="secondary"
            onClick={() => setDialog(DIALOG_STEPS.FILTER_SELECT)}
          >
            <FormattedMessage id="general.button.cancel" defaultMessage="Cancel" />
          </Button>
          <Button id="rename-filter-set-apply-button" onClick={handleRename} disabled={!isFormValid}>
            <FormattedMessage id="advanced-filter-rename-button" defaultMessage="Rename" />
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
